import type { Presence } from '@weave/schema-gen-ts/dist/schemas/phone/presence/v1/api.pb';
import type { DevicePresenceUpdateEvent } from '@weave/schema-gen-ts/dist/shared/phone/v1/presence/message.pb';

export type PresenceData = {
  userPresence: Presence[];
  devicePresence: Presence[];
};

export type MergePresenceOptions = {
  basePresence: PresenceData;
  targetedPresence?: PresenceData;
  deviceUpdates?: Map<string, DevicePresenceUpdateEvent>;
  enableLogging?: boolean;
};

/**
 * Merges targeted presence data from connection events into the base presence data
 */
export function mergeTargetedPresence(merged: PresenceData, targetedPresenceData: PresenceData): PresenceData {
  // Update users who match by userId
  targetedPresenceData.userPresence?.forEach((updatedUser) => {
    const index = merged.userPresence.findIndex((u) => u.userPresence?.userId === updatedUser.userPresence?.userId);
    if (index !== -1) {
      merged.userPresence[index] = updatedUser;
    }
  });

  targetedPresenceData.devicePresence?.forEach((updatedDevice) => {
    // Update standalone devices by sipProfileId
    const index = merged.devicePresence.findIndex(
      (d) => d.devicePresence?.sipProfileId === updatedDevice.devicePresence?.sipProfileId
    );
    if (index !== -1) {
      merged.devicePresence[index] = updatedDevice;
    }

    // Also update users whose attached device has the matching sipProfileId
    const sipProfileId = updatedDevice.devicePresence?.sipProfileId;
    if (sipProfileId) {
      merged.userPresence.forEach((user, _index) => {
        if (user.devicePresence?.sipProfileId === sipProfileId) {
          merged.userPresence[_index] = {
            ...user,
            devicePresence: updatedDevice.devicePresence ? { ...updatedDevice.devicePresence } : undefined,
          };
        }
      });
    }
  });

  return merged;
}

/**
 * Applies device presence updates from websocket events to the merged presence data
 */
export function applyDevicePresenceUpdates(
  merged: PresenceData,
  deviceUpdates: Map<string, DevicePresenceUpdateEvent>,
  enableLogging = false
): PresenceData {
  if (deviceUpdates.size === 0) return merged;

  const updates = Array.from(deviceUpdates.values());

  if (enableLogging) {
    console.log('[DevicePresence] Applying batch of updates:', {
      updateCount: updates.length,
      targetSipProfileIds: updates.map((u) => u.sipProfileId),
      userPresenceCount: merged.userPresence.length,
      devicePresenceCount: merged.devicePresence.length,
    });
  }

  // Create a map for O(1) lookup of updates by sipProfileId
  const updatesBySipId = new Map(updates.map((u) => [u.sipProfileId, u]));

  // Update in userPresence array (devices associated with users)
  merged.userPresence = merged.userPresence.map((user) => {
    const sipProfileId = user.devicePresence?.sipProfileId;
    if (!sipProfileId) return user;

    const update = updatesBySipId.get(sipProfileId);
    if (!update) return user;

    if (enableLogging) {
      console.log('[DevicePresence] Updating user card:', {
        userId: user.userPresence?.userId,
        userName: user.userPresence?.userName,
        sipProfileId,
        oldStatus: user.devicePresence?.deviceStatus,
        newStatus: update.deviceStatus,
      });
    }

    return {
      ...user,
      devicePresence: user.devicePresence
        ? {
            ...user.devicePresence,
            deviceStatus: update.deviceStatus,
            lastCallDurationSeconds: update.lastCallDurationSeconds,
            ongoingCallStartTimestamp: update.ongoingCallStartTime,
            lastCallEndTimestamp: update.lastCallEndTime,
          }
        : undefined,
    };
  }) as typeof merged.userPresence;

  // Update in devicePresence array (standalone devices)
  merged.devicePresence = merged.devicePresence.map((device) => {
    const sipProfileId = device.devicePresence?.sipProfileId;
    if (!sipProfileId) return device;

    const update = updatesBySipId.get(sipProfileId);
    if (!update) return device;

    if (enableLogging) {
      console.log('[DevicePresence] Updating device card:', {
        deviceName: device.devicePresence?.deviceName,
        sipProfileId,
        oldStatus: device.devicePresence?.deviceStatus,
        newStatus: update.deviceStatus,
      });
    }

    return {
      ...device,
      devicePresence: device.devicePresence
        ? {
            ...device.devicePresence,
            deviceStatus: update.deviceStatus,
            lastCallDurationSeconds: update.lastCallDurationSeconds,
            ongoingCallStartTimestamp: update.ongoingCallStartTime,
            lastCallEndTimestamp: update.lastCallEndTime,
          }
        : undefined,
    };
  }) as typeof merged.devicePresence;

  return merged;
}

/**
 * Main function to merge all presence data sources
 */
export function mergePresenceData({
  basePresence,
  targetedPresence,
  deviceUpdates,
  enableLogging = false,
}: MergePresenceOptions): PresenceData {
  let merged: PresenceData = {
    userPresence: [...(basePresence.userPresence || [])],
    devicePresence: [...(basePresence.devicePresence || [])],
  };

  // Apply targeted presence updates (connection events)
  if (targetedPresence) {
    merged = mergeTargetedPresence(merged, targetedPresence);
  }

  // Apply websocket device presence updates
  if (deviceUpdates && deviceUpdates.size > 0) {
    merged = applyDevicePresenceUpdates(merged, deviceUpdates, enableLogging);
  }

  return merged;
}

/**
 * Cleans up device update queue by removing updates for devices no longer in presence data
 */
export function cleanupDeviceUpdates(
  presenceData: PresenceData,
  currentUpdates: Map<string, DevicePresenceUpdateEvent>
): Map<string, DevicePresenceUpdateEvent> {
  if (currentUpdates.size === 0) return currentUpdates;

  // Collect all sipProfileIds that exist in current presence data
  const allSipIds = new Set<string>();
  presenceData.userPresence?.forEach((u) => {
    if (u.devicePresence?.sipProfileId) allSipIds.add(u.devicePresence.sipProfileId);
  });
  presenceData.devicePresence?.forEach((d) => {
    if (d.devicePresence?.sipProfileId) allSipIds.add(d.devicePresence.sipProfileId);
  });

  // Only keep updates for devices that are still in the data
  return new Map(Array.from(currentUpdates.entries()).filter(([sipId]) => allSipIds.has(sipId)));
}
